import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { ClientOnly } from "~/components/ui/client-only";
import { formatCurrency } from "~/lib/format";
import type { YearSummary } from "~/types/financials";

interface AssetCompositionChartProps {
	year: YearSummary;
}

const ASSET_COLORS = ["#0072FF", "#7517F8", "#A3CCFF"];
const LIABILITY_COLORS = ["#0E9F6E", "#FF9F45", "#F5426C"];

export function AssetCompositionChart({ year }: AssetCompositionChartProps) {
	const assetData = [
		{ name: "Stálá aktiva", value: year.fixedAssets },
		{ name: "Oběžná aktiva", value: year.currentAssets },
	].filter((d) => d.value != null && d.value > 0);

	const liabilityData = [
		{ name: "Vlastní kapitál", value: year.equity },
		{ name: "Dlouhodobé závazky", value: year.longTermLiabilities },
		{ name: "Krátkodobé závazky", value: year.currentLiabilities },
	].filter((d) => d.value != null && d.value > 0);

	// A pie chart cannot represent negative slices, so negative equity is
	// excluded from the "Pasiva" pie above. Surface it explicitly instead of
	// silently dropping it, otherwise the structure looks misleadingly wrong.
	const hasNegativeEquity = year.equity != null && year.equity < 0;

	if (assetData.length === 0 && liabilityData.length === 0 && !hasNegativeEquity) return null;

	return (
		<div className="card-surface rounded-xl p-6">
			<h3 className="text-lg font-semibold mb-4">Struktura majetku a zdrojů ({year.year})</h3>
			<ClientOnly fallback={<div className="h-[280px] animate-pulse bg-muted rounded" />}>
				<div className="grid grid-cols-2 gap-4">
					{assetData.length > 0 && (
						<div>
							<p className="text-sm font-medium text-center mb-2">Aktiva</p>
							<ResponsiveContainer width="100%" height={240}>
								<PieChart>
									<Pie
										data={assetData}
										dataKey="value"
										nameKey="name"
										cx="50%"
										cy="50%"
										outerRadius={80}
										label={false}
									>
										{assetData.map((_entry, idx) => (
											<Cell key={_entry.name} fill={ASSET_COLORS[idx % ASSET_COLORS.length]} />
										))}
									</Pie>
									<Tooltip formatter={(value) => formatCurrency((value as number) ?? null)} />
									<Legend fontSize={12} />
								</PieChart>
							</ResponsiveContainer>
						</div>
					)}
					{liabilityData.length > 0 && (
						<div>
							<p className="text-sm font-medium text-center mb-2">Pasiva</p>
							<ResponsiveContainer width="100%" height={240}>
								<PieChart>
									<Pie
										data={liabilityData}
										dataKey="value"
										nameKey="name"
										cx="50%"
										cy="50%"
										outerRadius={80}
										label={false}
									>
										{liabilityData.map((_entry, idx) => (
											<Cell
												key={_entry.name}
												fill={LIABILITY_COLORS[idx % LIABILITY_COLORS.length]}
											/>
										))}
									</Pie>
									<Tooltip formatter={(value) => formatCurrency((value as number) ?? null)} />
									<Legend fontSize={12} />
								</PieChart>
							</ResponsiveContainer>
						</div>
					)}
				</div>
				{hasNegativeEquity && (
					<p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
						Vlastní kapitál je záporný ({formatCurrency(year.equity)}) a není zobrazen v grafu
						pasiv. Záporný vlastní kapitál znamená, že závazky převyšují majetek společnosti.
					</p>
				)}
			</ClientOnly>
		</div>
	);
}
